// @ts-strict-ignore
import isNil from 'lodash/isNil';

import {FeatureGate, PropertyType, ReportName, ResourceType, SPECIAL_EVENTS} from 'common/report/constants';
import {Cohort as CohortType, EventDefinition, MpEvent, PropertyDefinition} from 'common/util/definitions-types';
import {Cohort} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {ActionType, ContextPanelData, DetailType, Metric} from 'common/widgets/context-panel/types';
import {getDefinitionForEvent, getDefinitionForProperty} from 'common/util/definitions';
import {formatMetricNumber} from 'common/util/index';
import {getImageSrcUrlBasedOnStorageBucket, LexiconEntityType, UserMediaType} from 'common/util/user-media-upload';
import {getEventsReportUrlForEvent, getReportUrl} from 'common/report/util';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {EntityType, EntityTypeNameSingular} from 'common/report/util/saved-entities/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {getShareCapability, isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {getTempLookupTableId} from 'common/report/stores/lookup-tables';
import {getIconForPropertyType, propertiesUtils} from 'common/report/util/properties';
import {getEventNameForEntityReferenceBrowse, renameEvent} from 'common/report/util/events';
import {cohortsStore} from 'common/report/stores/cohorts';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {Translatable} from 'common/i18n';
import {LocalizationStore} from 'common/i18n/store';
import {CurrentProject} from 'common/report/model-resources/projects';
import {getEscapedEntityName} from 'apps/lexicon/src/lexicon-app/utils';
import type {ProjectUsersResource} from 'common/report/model-resources/project-users';
import {convertProjectMemberToUser} from 'common/widgets/user-list/util';

import {
  getBorrowedProperty,
  isBehavioralCustomProperty,
  isInlineCustomProperty,
  isPersistedCustomProperty,
} from '../custom-property-modal/util';
import {UploadBuckets} from '../upload-media/types';
import {PresetDateRange} from '../date-range-picker/models/presets';

function isCustomPropertySaved(prop) {
  return !isNaN(prop.customPropertyId);
}

function getEventLink(event) {
  let pathBlock = ``;
  if (event.customEventId) {
    pathBlock = `/transformations/custom-events/$custom_event:${encodeURIComponent(event.customEventId)}`;
  } else if (event.mergedTo) {
    pathBlock = `/transformations/events/${encodeURIComponent(getEscapedEntityName(event.mergedTo))}`;
  } else {
    pathBlock = `/transformations/events/${encodeURIComponent(getEscapedEntityName(event.name))}`;
  }
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(`lexicon`, {projectId, workspaceId}, true) + pathBlock;
}

function getEventImageLink(eventDefinition: EventDefinition) {
  let imageUrl = null;
  const mediaPath = eventDefinition.event_media_path;
  const mediaType = eventDefinition.event_media_type;
  if (mediaPath && mediaPath !== `` && mediaType === UserMediaType.Image) {
    imageUrl = getImageSrcUrlBasedOnStorageBucket(mediaPath, UploadBuckets.Lexicon, LexiconEntityType.Events);
  }
  return imageUrl;
}

export function getPropertyLink(
  property,
  {dataGroupName}: {dataGroupName?: string} = {
    dataGroupName: null,
  },
) {
  /* 4 cases:
    1) custom profile property:     `/transformations/custom-profile-properties/$custom_property:<propId>`
    2) non-custom profile property:
      a) nonnull group name:        `/transformations/profile-properties/<groupName>/<propId>`
      b) null group name:           `/transformations/profile-properties/user-profiles/<propId>`
    3) custom event property:       `/transformations/custom-event-properties/$custom_property:<propId>`
    4) non-custom event property:   `/transformations/event-properties/<propId>`
  */
  const customPrefix = property.customPropertyId ? `custom-` : ``;
  const resourceType = property.resourceType.toLocaleLowerCase() === ResourceType.User ? `profile` : `event`;
  const propertyType = `${customPrefix}${resourceType}`;

  // Use mergedTo property name if this property is merged
  const propertyName = property.mergedTo || property.name;

  const propertyComponent = property.customPropertyId
    ? `$custom_property:${property.customPropertyId}`
    : getEscapedEntityName(propertyName);
  const optionalPropertyGroup =
    !property.customPropertyId && property.resourceType === ResourceType.User
      ? `/` + (dataGroupName?.toLocaleLowerCase().replace(/\s/g, `-`) ?? `user-profiles`)
      : ``;

  const pathBlock = `/transformations/${propertyType}-properties${optionalPropertyGroup}/${encodeURIComponent(propertyComponent)}`;

  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(`lexicon`, {projectId, workspaceId}, true) + pathBlock;
}

function getCohortLink(cohort: CohortType) {
  const cohortId = Number.isInteger(cohort.value) ? cohort.value : cohort.id;

  // Extract serialized cohort for inline cohorts
  const serializedCohort =
    cohort.value && typeof cohort.value !== `number` && cohort.value.cohort ? cohort.value.cohort : undefined;

  // Guard: Must have either a valid cohortId OR serializedCohort
  if (!Number.isInteger(cohortId) && !serializedCohort) {
    console.warn(`Cannot generate cohort link: missing cohortId and serializedCohort`, cohort);
    // Return null instead of crashing
    return null;
  }

  const url = Cohort.getExploreHashForUsersInCohort({
    cohortId: cohortId as number,
    dataGroupId: cohort.dataGroupId,
    cohortName: cohort.name,
    serializedCohort,
  });
  const projectId = getProjectId();
  const workspaceId = getWorkspaceId();
  return getReportUrl(ReportName.Users, {projectId, workspaceId}, true) + `${url}`;
}

function shareEnabled() {
  return currentProjectStore.value?.getFeatureGate(FeatureGate.Sharing);
}

// TODO opslead
export function getTooltipDataForEvent(
  mpEvent: MpEvent,
  eventDefinitions: EventDefinition[],
  {
    projectUsers,
    allowEdit = true,
    allowDescriptionEdit = false,
    sampleEventUrl = ``,
  }: {
    projectUsers: ProjectUsersResource;
    allowEdit?: boolean;
    allowDescriptionEdit?: boolean;
    sampleEventUrl?: string;
  },
): ContextPanelData {
  const def = getDefinitionForEvent(mpEvent, eventDefinitions);

  if (!def) {
    return null;
  }

  const details: ContextPanelData[`details`] = [];
  const metrics: Metric[] = [];
  const extraInfo: ContextPanelData[`extraInfo`] = [];
  const actions: ContextPanelData[`actions`] = [];
  const excludedMetricIcons = [];
  let tags = [];
  const imageInfo = [];

  let editItem = null;

  const header = def.mergedTo
    ? ({t}) => t(`query-builder.context-menu.merged-event-name`, {mergedToEventName: def.mergedTo})
    : renameEvent(def.name);
  const description = def.mergedTo
    ? ({t}) => t(`query-builder.context-menu.merged-event-description`, {mergedToEventName: def.mergedTo})
    : def.description;

  const entityType = def.customEventId ? EntityType.CustomEvent : EntityType.Event;
  const entityIcon = def.customEventId ? `custom` : `event`;

  if (allowDescriptionEdit) {
    const type = def.customEventId ? EntityTypeNameSingular.CustomEvent : EntityTypeNameSingular.Event;
    const id = def.customEventId ? def.customEventId : def.id;
    editItem = {id, type, name: def.name};
  }

  if (def.uiUniqueQueryCount) {
    const value = def.uiUniqueQueryCount;
    metrics.push({
      icon: `person`,
      tooltip: ({t}) => t(`query-builder.context-menu.usage-count`, {count: value}),
      value,
    });
  }

  if (def.uiQueryCount) {
    const value = def.uiQueryCount;
    const valueFormatted = formatMetricNumber(value);
    metrics.push({
      icon: `pie`,
      tooltip: ({t}) => t(`query-builder.context-menu.query-count`, {count: value}),
      value: valueFormatted,
      ...(def.uiQueryCountRank
        ? {
            rank: def.uiQueryCountRank,
            rankToolTip: ({t}) => t(`query-builder.context-menu.query-rank`, {rank: def.uiQueryCountRank}),
          }
        : {}),
    });
  }

  if (def.count) {
    const value = def.count;
    const valueFormatted = formatMetricNumber(value);
    metrics.push({
      icon: `pulse`,
      tooltip: ({t}) => t(`query-builder.context-menu.track-count`, {count: value}),
      value: valueFormatted,
    });
  }

  if (!Object.values(SPECIAL_EVENTS).includes(def.name)) {
    if (def.mergedTo) {
      sampleEventUrl = getEventsReportUrlForEvent({
        projectId: getProjectId(),
        workspaceId: getWorkspaceId(),
        event: {value: def.mergedTo, label: def.mergedTo},
        presetDateRange: PresetDateRange.Last30Days,
      });
    }
    actions.push({
      name: ActionType.ViewSampleEvents,
      label: ({t}) => t(`query-builder.context-menu.view-sample-events`),
      icon: `eye-on`,
      item: {
        id: def.id,
        name: def.name,
        url: sampleEventUrl,
      },
    });
  }

  if (allowEdit && def.customEventId) {
    // Allow context share button for custom events
    if (shareEnabled()) {
      actions.push({
        name: ActionType.ShareCustomEvent,
        label: ({t}) => t(`query-builder.context-menu.share`),
        icon: `invite`,
        // Only superadmin or editor can share
        disabled: !getShareCapability(def).enableShare,
        disabledTooltipText: ({t}) => t(`query-builder.context-menu.share-no-access`),
        item: {
          id: def.customEventId || def.collectEverythingEventId,
          superadminOverrideShare: getShareCapability(def).requireSuperadminOverride,
        },
      });
    }
    if (def.customEventId) {
      excludedMetricIcons.push(`pulse`);
    }

    const hasEditPerms = def.can_update_basic || isAllowedSuperadminOverride(def);

    actions.push({
      name: ActionType.EditCustomEvent,
      label: ({t}) => t(`query-builder.context-menu.edit-custom-event`),
      icon: `compose`,
      item: {
        id: def.customEventId || def.collectEverythingEventId,
      },
      disabled: !hasEditPerms,
      disabledTooltipText: hasEditPerms ? `` : ({t}) => t(`query-builder.context-menu.edit-no-access`),
    });
  }
  actions.push({
    name: ActionType.ViewEntity,
    label: ({t}) => t(`query-builder.context-menu.view-in-lexicon`),
    icon: `share`,
    item: {
      id: def.id,
      name: def.name,
      url: getEventLink(def),
    },
  });

  if (def.platforms && def.platforms.length) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.platforms`),
      value: def.platforms,
      type: DetailType.TextHighlight,
    });
  }

  if (def.tags && def.tags.length) {
    tags = def.tags;
  }

  const imageUrl = getEventImageLink(def);
  if (imageUrl) {
    // Using image info here to pass to context panel to allow to easily expand to multiple images
    imageInfo.push({imageUrl});
  }

  if (def.isMixpanelDefinition) {
    extraInfo.push(({t}) => t(`query-builder.context-menu.automatically-tracked`));
  }

  if (def.collectEverythingEventId) {
    extraInfo.push(({t}) => t(`query-builder.context-menu.codeless-tracking`));
  }
  let queriedByUsers = [];

  if (def.customEventId && def.creatorInfo?.email) {
    const userInfo = {id: def.creatorId, email: def.creatorInfo?.email, name: def.creatorInfo.name};
    details.push({name: ({t}) => t(`query-builder.context-menu.creator`), type: DetailType.User, value: {...userInfo}});
  }

  const contacts = projectUsers.getFilteredMembers({emails: def.contacts}).map(convertProjectMemberToUser);
  if (contacts.length) {
    details.push({
      type: DetailType.UserList,
      name: ({t}) => t(`query-builder.context-menu.owner`, {context: `plural`}),
      value: contacts,
    });
  }

  if (def.displayName) {
    details.push({name: ({t}) => t(`query-builder.context-menu.tracked-as`), value: def.name});
  }

  if (def?.queriedByUsers) {
    const userId = userCookieUtils.getUserIdentityFromCookie()?.id;
    queriedByUsers = def?.queriedByUsers;

    const userIdx = queriedByUsers.findIndex((user) => user.id === userId);
    if (userIdx > 0) {
      // moving current user to the begining
      [queriedByUsers[0], queriedByUsers[userIdx]] = [queriedByUsers[userIdx], queriedByUsers[0]];
    }
  }

  if (!isNil(def.reportsCount)) {
    const entityNameForBrowse = getEventNameForEntityReferenceBrowse(def);
    details.push({
      name: ({t}) => t(`query-builder.context-menu.used-in-reports`),
      value: {count: def.reportsCount, entityNameForBrowse},
      type: DetailType.BrowseLink,
    });
  }

  details.push({
    name: ({t}) => t(`query-builder.context-menu.session-replay`),
    value: def.name,
    type: DetailType.SessionReplayPreview,
  });

  return {
    header,
    entityType,
    entityIcon,
    entityReferencesCount: def.reportsCount,
    entityName: def.name,
    description,
    extraInfo,
    editItem,
    details,
    metrics,
    excludedMetricIcons,
    actions,
    queriedByUsers,
    tags,
    imageInfo,
    verified: def.verified,
    verifiedBy: def.lastVerifiedBy?.name,
    verifiedAt: def.lastVerified,
  };
}

// TODO opslead

type PropertyTypeOptions = {
  allowEdit: boolean;
  lookupTables: Obj;
  allowDescriptionEdit: boolean;
  iconReferenceType?: `resourceType` | `dataType`;
  currentProject?: CurrentProject;
};

export function getTooltipDataForProperty(
  {
    name,
    resourceType,
    dataType,
    i18n,
    path = [],
    customPropertyId = null,
    dataGroupId = null,
    dataGroupName = null,
    isLookupTableProperty = false,
    isObjectInnerKey = false,
    isRecentlyUsed = false,
  }: {
    name: string; // raw value of the property or formatted name of the lookup property
    resourceType: ResourceType;
    dataType: PropertyType;
    i18n: LocalizationStore;
    path?: string[];
    customPropertyId?: string;
    dataGroupId?: string;
    dataGroupName?: string;
    isLookupTableProperty?: boolean;
    isObjectInnerKey?: boolean;
    isRecentlyUsed?: boolean;
  },
  propertyDefinitions: PropertyDefinition[],
  {allowEdit, lookupTables, allowDescriptionEdit, iconReferenceType}: PropertyTypeOptions = {
    allowEdit: true,
    lookupTables: {},
    allowDescriptionEdit: false,
    iconReferenceType: `resourceType`,
  },
): ContextPanelData {
  // An object property's inner keys are bare leaf names with no Lexicon
  // definitions of their own, so a same-named top-level property's definition
  // must not supply the card's metadata; callers fall back to a plain card.
  if (isObjectInnerKey) {
    return null;
  }

  let def: PropertyDefinition;
  if (isLookupTableProperty) {
    def = {
      name,
      resourceType,
      description: ``,
      type: dataType,
      apiQueryCount: 0,
      uiQueryCount: 0,
    };
    allowDescriptionEdit = false;
  } else {
    def = getDefinitionForProperty({name, customPropertyId, dataGroupId}, propertyDefinitions);
  }

  if (!def) {
    return null;
  }

  const metrics: Metric[] = [];
  const details = [];
  const actions = [];
  const header = def.mergedTo
    ? ({t}) => t(`query-builder.context-menu.merged-property-name`, {mergedToPropertyName: def.mergedTo})
    : propertiesUtils.renameProperty(def.name, def.resourceType, i18n);
  const description = def.mergedTo
    ? ({t}) => t(`query-builder.context-menu.merged-property-description`, {mergedToPropertyName: def.mergedTo})
    : def.description;
  const borrowedProperty = getBorrowedProperty(def.customProperty);
  let subHeader: Translatable | null = null;
  if (isLookupTableProperty) {
    subHeader = ({t}) =>
      t(`query-builder.context-menu.from-lookup-table`, {lookupTableName: lookupTables[dataGroupId].name});
  } else if (borrowedProperty) {
    subHeader = ({t}) =>
      t(`query-builder.context-menu.from-borrowed-property`, {
        borrowedEventName: renameEvent(borrowedProperty.borrowEvent?.value),
      });
  }
  const extraInfo = [];
  const excludedMetricIcons = [];

  let entityType = EntityType.Property;
  let entityIcon = ``;

  const isCustomProperty = Boolean(def.customPropertyId);

  let propertyType = def.type;
  switch (iconReferenceType) {
    case `dataType`: {
      if (propertyType) {
        if (propertyType === `dimension`) {
          propertyType = def.customProperty?.joinPropertyType || def.join_property_type;
        }
        entityIcon = getIconForPropertyType(propertyType, {isCustom: isCustomProperty, isScd: !!def.scdEvent});
      }
      break;
    }
    case `resourceType`:
      entityIcon = def.resourceType === `user` ? `profile-attribute` : `event-attribute`;
      break;
  }

  if (isCustomProperty && isBehavioralCustomProperty(def.customProperty)) {
    entityType = EntityType.Behavior;
    allowDescriptionEdit = false;
  }
  if (isCustomProperty && !isCustomPropertySaved(def)) {
    allowDescriptionEdit = false;
    extraInfo.push(({t}) => t(`query-builder.context-menu.property-is-local`));
  }

  let editItem = null;
  if (allowDescriptionEdit && !def.customPropertyId) {
    const type =
      def.resourceType === `user` ? EntityTypeNameSingular.UserProperty : EntityTypeNameSingular.EventProperty;
    editItem = {id: def.id, type, name: def.name, dataGroupId};
  }

  if (def.uiUniqueQueryCount) {
    const value = def.uiUniqueQueryCount;
    metrics.push({
      icon: `person`,
      tooltip: ({t}) => t(`query-builder.context-menu.usage-count`, {count: value}),
      value,
    });
  }

  if (def.displayName) {
    details.push({name: ({t}) => t(`query-builder.context-menu.tracked-as`), value: def.name});
  }

  if (def.uiQueryCount || def.apiQueryCount) {
    const value = def.uiQueryCount + def.apiQueryCount;
    const valueFormatted = formatMetricNumber(value);
    metrics.push({
      icon: `pie`,
      tooltip: ({t}) => t(`query-builder.context-menu.usage-times`, {count: value}),
      value: valueFormatted,
    });
  }

  if (def.count) {
    const value = def.count;
    const valueFormatted = formatMetricNumber(value);
    const tooltip =
      def.resourceType === ResourceType.User
        ? ({t}) => t(`query-builder.context-menu.users-usage`, {count: value})
        : ({t}) => t(`query-builder.context-menu.events-usage`, {count: value});
    metrics.push({
      icon: `pulse`,
      tooltip,
      value: valueFormatted,
    });
  }

  if (def.exampleValue) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.example`),
      value: [def.exampleValue],
      type: DetailType.TextHighlight,
    });
  }

  const isUnsaved = def.customPropertyId && !isCustomPropertySaved(def);

  if (def && !isUnsaved) {
    actions.push({
      name: ActionType.ViewEntity,
      label: ({t}) => t(`query-builder.context-menu.view-in-lexicon`),
      icon: `share`,
      item: {
        id: def.id,
        name: def.name,
        url: getPropertyLink(def, {dataGroupName}),
      },
    });
    if (def.customProperty?.userInfo) {
      excludedMetricIcons.push(`pulse`);
      details.push({
        name: ({t}) => t(`query-builder.context-menu.creator`),
        type: DetailType.User,
        value: {...def.customProperty.userInfo},
      });
    }
  }

  if (allowEdit && def.customPropertyId) {
    // TODO - scobb - initiate superadmin flow?
    // eslint-disable-next-line camelcase
    const hasPerms =
      !isPersistedCustomProperty(def.customPropertyId) ||
      def.customProperty?.canUpdateBasic ||
      isAllowedSuperadminOverride(def.customProperty);

    const disabled = !hasPerms;
    const extraActionParams = {
      disabled,
      disabledTooltipText: hasPerms ? `` : ({t}) => t(`query-builder.context-menu.edit-no-access`),
    };
    if (allowDescriptionEdit && hasPerms) {
      editItem = {id: Number(def.customPropertyId), type: EntityTypeNameSingular.CustomProperty, name: def.name};
    }

    if (
      shareEnabled() &&
      !isBehavioralCustomProperty(def.customProperty) &&
      !isInlineCustomProperty(def.customPropertyId)
    ) {
      actions.push({
        name: ActionType.ShareCustomProperty,
        label: ({t}) => t(`query-builder.context-menu.share`),
        disabled: isUnsaved || !getShareCapability(def.customProperty).enableShare,
        disabledTooltipText: isUnsaved
          ? ({t}) => t(`query-builder.context-menu.property-save`)
          : ({t}) => t(`query-builder.context-menu.share-no-access`),
        icon: `invite`,
        item: {
          property: {
            customPropertyId: def.customPropertyId,
            customProperty: def.customProperty,
          },
          superadminOverrideShare: getShareCapability(def.customProperty).requireSuperadminOverride,
        },
      });
    }

    actions.push({
      name: ActionType.EditCustomProperty,
      label: ({t}) => t(`query-builder.context-menu.property-edit`),
      icon: `compose`,
      item: {
        property: {
          customPropertyId: def.customPropertyId,
          customProperty: def.customProperty,
        },
      },
      ...extraActionParams,
    });
  }

  const tempDataGroupId = getTempLookupTableId({
    value: name,
    resourceType: def.resourceType,
    customPropertyId: Number(def.customPropertyId) || undefined,
  });
  let lookupTableId;
  let lookupTableIsTempMapping = false;
  if (tempDataGroupId) {
    lookupTableId = tempDataGroupId;
    lookupTableIsTempMapping = true;
  } else if (def.customProperty?.mappedDataGroupId) {
    lookupTableId = def.customProperty.mappedDataGroupId;
  } else if (def[`dimension_data_group_id`]) {
    lookupTableId = def[`dimension_data_group_id`];
  }

  // Ensure that lookupTableId is not referring to a group property
  // (group properties currently do not support Lookup table mappings so by definition they won't have a data group id that corresponds to a lookup table)
  if (lookupTableId && lookupTableId !== dataGroupId) {
    actions.push({
      name: ActionType.LookupTableInfo,
      label: ({t}) => t(`query-builder.context-menu.lookup-table-info`),
      icon: `info`,
      item: {
        id: def.id,
        name: def.name,
        url: `https://help.mixpanel.com/hc/en-us/articles/360044139291-Lookup-tables`,
      },
    });
    details.push({
      name: `Mapped Lookup Table${lookupTableIsTempMapping ? ` (Temp)` : ``}`,
      value: [lookupTables?.[lookupTableId]?.name],
    });
  }

  if (def.scdEvent) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.from-history-table`),
      value: [def.scdEvent],
    });

    extraInfo.push(({t}) => t(`query-builder.context-menu.user-query-scd-notice`));
  }

  return {
    header,
    subHeader,
    path,
    isUnsaved,
    entityIcon,
    entityType,
    description,
    editItem,
    metrics,
    details,
    actions,
    isRecentlyUsed,
    excludedMetricIcons,
    extraInfo,
  };
}

export function getTooltipDataForCohort(
  cohort: CohortType,
  {allowDescriptionEdit = false, enableVerifiedCohorts = false} = {},
): ContextPanelData {
  const isInlineCohort = !!(
    cohort.value &&
    typeof cohort.value !== `number` &&
    cohort.value.cohort &&
    cohort.value.cohort.raw_cohort
  );
  if (!cohort.name && !isInlineCohort) {
    return null;
  }

  const details = [];
  const metrics: Metric[] = [];
  const extraInfo = [];
  const actions = [];

  if (!isInlineCohort) {
    const cohortUrl = getCohortLink(cohort);

    // Only add "View in Users" action if we can generate a valid link
    if (cohortUrl) {
      actions.push({
        name: ActionType.ViewEntity,
        label: ({t}) => t(`query-builder.context-menu.view-in-users`),
        icon: `share`,
        item: {
          id: cohort.id,
          name: cohort.name,
          url: cohortUrl,
        },
      });
    }
    //fix for cohort item being a proxy element and not having all the elements
    const createdBy = cohort?.createdBy || cohortsStore.value[cohort.value as any]?.created_by;
    details.push({
      name: ({t}) => t(`query-builder.context-menu.creator`),
      type: DetailType.User,
      value: {...createdBy},
    });

    if (cohort.value) {
      details.push({name: ({t}) => t(`query-builder.context-menu.cohort-id`), value: cohort.value});
    }
  }

  if (Number.isInteger(cohort.count)) {
    const descriptor = cohort.dataGroupId ? `unique` : `user`;
    const valueFormatted = cohort.count?.toLocaleString();
    const countLabel = cohort.count === 1 ? `${valueFormatted} ${descriptor}` : `${valueFormatted} ${descriptor}s`;
    metrics.push({icon: `community`, tooltip: `${countLabel} in cohort`, value: valueFormatted});
  }

  let editItem = null;
  const canUpdateBasic = cohort.canUpdateBasic ?? cohort.can_update_basic ?? false;
  if (!isInlineCohort && canUpdateBasic && allowDescriptionEdit) {
    editItem = {id: cohort.value, type: `cohort`};
  }

  // inline cohort is not considered as a saved entity. A user should always be able to
  // edit an inline cohort but not be able to share.
  if (cohort.value) {
    // inline cohort is not considered as a saved entity thus not shareable
    if (shareEnabled()) {
      actions.push({
        name: ActionType.ShareCohort,
        icon: `invite`,
        label: ({t}) => t(`query-builder.context-menu.share`),
        // Only superadmin or editor can share
        disabled: isInlineCohort || !(cohort.isSuperadmin || cohort.canShare),
        disabledTooltipText: isInlineCohort
          ? ({t}) => t(`query-builder.context-menu.cohort-save`)
          : ({t}) => t(`query-builder.context-menu.share-no-access`),
        item: isInlineCohort
          ? {
              ...cohort,
              superadminOverrideShare: getShareCapability(cohort).requireSuperadminOverride,
            }
          : {
              id: cohort.value,
              dataGroupId: cohort.dataGroupId,
              superadminOverrideShare: getShareCapability(cohort).requireSuperadminOverride,
            },
      });
    }

    const hasEditPerms = isInlineCohort || canUpdateBasic || isAllowedSuperadminOverride(cohort);

    actions.push({
      name: ActionType.EditCohort,
      icon: hasEditPerms ? `compose` : `eye-on`,
      label: hasEditPerms
        ? ({t}) => t(`query-builder.context-menu.cohort-edit`)
        : ({t}) => t(`query-builder.context-menu.cohort-view`),
      disabled: false,
      item: isInlineCohort
        ? cohort
        : {
            id: cohort.value,
            dataGroupId: cohort.dataGroupId,
          },
    });
  }

  const storedCohort = enableVerifiedCohorts && !isInlineCohort ? cohortsStore.value[cohort.value as any] : undefined;
  const isVerified = !!(storedCohort && `verified` in storedCohort && storedCohort.verified);
  const verifiedBy = isVerified && storedCohort && `verified_by` in storedCohort ? storedCohort.verified_by : undefined;
  const lastVerified =
    isVerified && storedCohort && `last_verified` in storedCohort ? storedCohort.last_verified : undefined;
  if (isVerified && verifiedBy) {
    details.push({
      name: ({t}) => t(`query-builder.context-menu.verified-by`),
      type: DetailType.User,
      value: {name: verifiedBy.name, email: verifiedBy.email},
    });
  }

  return {
    header: cohort.name,
    entityType: EntityType.Cohort,
    entityIcon: `community`,
    isUnsaved: isInlineCohort,
    description: cohort.description,
    editItem,
    metrics,
    details,
    extraInfo,
    excludedMetricIcons: [`pulse`, `pie`],
    actions,
    verified: isVerified,
    verifiedBy: verifiedBy?.name,
    verifiedAt: lastVerified,
  };
}

export function trackDiscoverBackLinkClicked({eventName, reportsCount}) {
  mixpanel.track(`[Context Panel] Click used in button`, {
    'Event Name': eventName,
    'Reports count': reportsCount,
  });
}
